// React
import { memo, useEffect } from 'react'

// Components
import { BaseDialog, FormLabel, RoleMultipleSelectOption } from 'components'

// Interfaces
import { IChangeRoleDialogProps } from './interfaces'

// Utils
import { messageUtils_error } from 'utils/common/message.utils'

// i18n
import { useTranslation } from 'react-i18next'

// Lodash
import cloneDeep from 'lodash/cloneDeep'

// Antd
import { Form, Row, Col } from 'antd'

const ChangeRoleDialog = memo(
  ({ isOpen, loading, attrs, onClickFirstButton, onClickCloseButton }: IChangeRoleDialogProps) => {
    // Hook
    const { t } = useTranslation()
    const [form] = Form.useForm()

    // Do when user open the dialog
    useEffect(() => {
      if (isOpen) form.setFieldsValue({
        roles: attrs.roles?.map(role => {
          return { role: role.id?.toString(), roleName: role.name };
        })
      })

      return () => {
        onClear();
      };
      // eslint-disable-next-line
    }, [isOpen])

    /**
     * @description Clear state from current component
     *
     * @return {void} void
     */
    const onClear = (): void => {
      form.resetFields()
    }

    /**
     * @description Watch any change when form is submitted
     *
     * @param {{roleIds: number[]}} form
     *
     * @return {void} void
     */
    const onFinish = (form: { roleIds: number[] }): void => {
      onClickFirstButton(form)
    }

    /**
     * @description Check before emitting the onClickFirstButton method
     *
     * @return {void} void
     */
    const onBeforeClickFirstButton = async (): Promise<any> => {
      try {
        const response = await form.validateFields()
        const clonedResponse = {
          roleIds: cloneDeep(response.roles.map((role: { role?: any }) => parseInt(role?.role))),
        }

        onFinish(clonedResponse)
      } catch (err: any) {
        return messageUtils_error(t('app.validations.formNeedValidation'))
      }
    }

    /**
     * @description Render body
     *
     * @return {JSX.Element} JSX.Element
     */
    const renderBody = (): JSX.Element => {
      return (
        <Form
          form={form}
          name="user-approve-registration-form"
          onFinish={onBeforeClickFirstButton}
          scrollToFirstError
          autoComplete="off"
          requiredMark={false}
          initialValues={{ roles: [undefined] }}
        >
          <Row>
            <Col xs={24} sm={24} md={4} lg={4} xl={4}>
              <FormLabel label="user.update.form.role" />
            </Col>
            <Col xs={24} sm={24} md={16} lg={16} xl={16}>
              <RoleMultipleSelectOption
                loading={attrs.isRoleLoading}
                formName="roles"
                roleDropdown={attrs.roleDropdown}
                form={form}
              />
            </Col>
          </Row>
        </Form>
      )
    }

    return (
      <BaseDialog
        title="user.update.changeRole"
        isOpen={isOpen}
        body={renderBody()}
        firstButtonLabel="user.update.save"
        onFirstButtonClick={onBeforeClickFirstButton}
        onButtonCloseClick={() => onClickCloseButton(false)}
        isFirstButtonLoading={loading || attrs.isUserActionLoading}
        isUsingDefaultFooter
        forceRender
        closable
        width="673px"
      />
    )
  },
)

export { ChangeRoleDialog }
